import React, { useCallback, useEffect, useState } from 'react';
import { solutionClaimsService } from '../../services/solutionClaimsService';
import type { SolutionClaim } from '../../services/subProjectService';
import type { ClaimCategory } from '../../services/claimableItemsService';
import { CAT_LABELS, CAT_COLORS, CAT_BG, btnPrimary, btnSecondary, btnDanger } from './claimsConstants';

export interface ClaimsReviewStepProps {
  subProjectId: string;
  solutionId: string;
  reloadTrigger: number;
  t: (k: string, fb?: string) => string;
}

const ClaimsReviewStep: React.FC<ClaimsReviewStepProps> = ({ subProjectId, solutionId, reloadTrigger, t }) => {
  const [claims, setClaims] = useState<SolutionClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingClaim, setEditingClaim] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await solutionClaimsService.list(subProjectId, solutionId);
      setClaims(data);
    } catch {
      setError(t('claims.loadError', 'Failed to load claims'));
    } finally { setLoading(false); }
  }, [subProjectId, solutionId, t]);

  useEffect(() => { load(); }, [load, reloadTrigger]);

  const handleSave = async (claimId: string) => {
    const newText = editingClaim[claimId];
    if (!newText) return;
    setSaving(prev => new Set(prev).add(claimId));
    try {
      const updated = await solutionClaimsService.update(subProjectId, solutionId, claimId, { claim_text: newText });
      setClaims(prev => prev.map(c => c.id === claimId ? updated : c));
      setEditingClaim(prev => { const n = { ...prev }; delete n[claimId]; return n; });
    } catch {
      setError(t('claims.saveError', 'Failed to save claim'));
    } finally {
      setSaving(prev => { const n = new Set(prev); n.delete(claimId); return n; });
    }
  };

  const handleDelete = async (claimId: string) => {
    try {
      await solutionClaimsService.delete(subProjectId, solutionId, claimId);
      setClaims(prev => prev.filter(c => c.id !== claimId));
    } catch { setError(t('claims.deleteError', 'Failed to delete claim')); }
  };

  if (loading) return <div style={{ padding: 16, fontSize: 11, color: '#94a3b8' }}>{t('common.loading', 'Loading…')}</div>;

  return (
    <div style={{ padding: '12px 16px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', flex: 1 }}>
          {t('claimsWorkspace.step4.title', 'Review & Edit Claims')} {claims.length > 0 && `(${claims.length})`}
        </span>
        <button onClick={load} style={btnSecondary}>{t('common.refresh', 'Refresh')}</button>
      </div>
      {error && (
        <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>{error}</div>
      )}
      {claims.length === 0 && (
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          {t('claimsWorkspace.step4.empty', 'No claims yet. Go back to Step 4 to draft them.')}
        </div>
      )}
      {claims.map(claim => {
        const editing = editingClaim[claim.id] !== undefined;
        const isSaving = saving.has(claim.id);
        const priorArtUsed = (claim as unknown as Record<string, unknown>).prior_art_used as Array<{ patent_id?: string; document_id: string }> | undefined;
        return (
          <div key={claim.id} style={{ border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: claim.is_independent ? '#f8fafc' : '#fff' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', minWidth: 18 }}>{claim.claim_number}.</span>
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: CAT_BG[claim.category as ClaimCategory] ?? '#e0e7ff', color: CAT_COLORS[claim.category as ClaimCategory] ?? '#3730a3', fontWeight: 700, textTransform: 'uppercase' }}>
                {CAT_LABELS[claim.category as ClaimCategory] ?? claim.category}
              </span>
              {!claim.is_independent && (
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{t('claims.dependsOn', 'dep.')} {claim.depends_on?.join(', ')}</span>
              )}
              <div style={{ flex: 1 }} />
              {priorArtUsed && priorArtUsed.length > 0 && (
                <span style={{ fontSize: 9, color: '#64748b', fontStyle: 'italic' }}>
                  {t('claimsWorkspace.step4.draftedUsing', 'via:')} {priorArtUsed.map(p => p.patent_id || p.document_id.slice(0, 8)).join(', ')}
                </span>
              )}
              <button onClick={() => setEditingClaim(prev => ({ ...prev, [claim.id]: claim.claim_text }))} style={{ ...btnSecondary, padding: '1px 7px', fontSize: 10 }}>
                {t('common.edit', 'Edit')}
              </button>
              <button onClick={() => handleDelete(claim.id)} style={btnDanger}>{t('common.delete', 'Delete')}</button>
            </div>
            <div style={{ padding: '5px 8px 6px 32px' }}>
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <textarea value={editingClaim[claim.id]}
                    onChange={e => setEditingClaim(prev => ({ ...prev, [claim.id]: e.target.value }))}
                    style={{ width: '100%', fontSize: 11, fontFamily: 'inherit', border: '1px solid #94a3b8', borderRadius: 3, padding: '4px 6px', resize: 'vertical', minHeight: 80, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => handleSave(claim.id)} disabled={isSaving} style={btnPrimary}>{t('common.save', 'Save')}</button>
                    <button onClick={() => setEditingClaim(prev => { const n = { ...prev }; delete n[claim.id]; return n; })} style={btnSecondary}>{t('common.cancel', 'Cancel')}</button>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 11, color: '#334155', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{claim.claim_text}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClaimsReviewStep;
